import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Tour from 'reactour'
import {Steps} from './Steps'
import styled from 'styled-components'

const TourUI = styled(Tour)`
	max-width: 78%;

	.sc-dIfARi {
		justify-content: space-between;
	}
	.sc-fnGiBr {
		margin: 0;
	}
`

export default function AppTour({
	steps = Steps,
	storageKey = 'tour_seen_v1',
	autoOpen = true,
	once = true,
	tourProps = {},
}) {
	/* -----------------------------------------------------------------------
     1) Basic open/close + "Don't show again"
  ----------------------------------------------------------------------- */
	const seen =
		typeof window !== 'undefined' && localStorage.getItem(storageKey)
	const [isOpen, setIsOpen] = useState(() => autoOpen && (!once || !seen))
	const [dontShowAgain, setDontShowAgain] = useState(true)

	const handleClose = useCallback(() => {
		if (once && dontShowAgain && typeof window !== 'undefined') {
			localStorage.setItem(storageKey, '1')
		}
		setIsOpen(false)
		if (typeof tourProps.onFinish === 'function') tourProps.onFinish()
	}, [dontShowAgain, once, storageKey, tourProps])

	/* -----------------------------------------------------------------------
     2) Wrap step content with controls (NO scroll changes)
  ----------------------------------------------------------------------- */
	const toSelector = useCallback(t => {
		if (!t) return ''
		if (
			t.startsWith('#') ||
			t.startsWith('.') ||
			t.startsWith('[') ||
			t.includes(' ') ||
			t.includes('>') ||
			t.includes(':') ||
			t.includes('#') ||
			t.includes('.')
		)
			return t
		return `#${t}`
	}, [])

	const computedSteps = useMemo(() => {
		return steps.map(s => {
			const original = s.content
			const wrapped = args => (
				<div style={{maxWidth: 360, paddingTop: 20, fontSize: 14}}>
					{typeof original === 'function' ? original(args) : original}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 12,
							marginTop: 10,
							fontSize: 12,
						}}
					>
						<label
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 6,
							}}
						>
							<input
								type="checkbox"
								checked={dontShowAgain}
								onChange={e =>
									setDontShowAgain(e.target.checked)
								}
							/>
							Don’t show again
						</label>
						<button
							onClick={args.close}
							style={{
								padding: 0,
								border: 'none',
								background: 'transparent',
								color: '#0ec0e2',
								textDecoration: 'underline',
								cursor: 'pointer',
							}}
						>
							Skip tour
						</button>
					</div>
				</div>
			)
			return {...s, selector: toSelector(s.target), content: wrapped}
		})
	}, [steps, dontShowAgain, toSelector])

	/* -----------------------------------------------------------------------
     3) Render (NO getCurrentStep, NO manual scroll, NO body locking)
  ----------------------------------------------------------------------- */
	return (
		<TourUI
			steps={computedSteps}
			isOpen={isOpen}
			onRequestClose={handleClose}
			accentColor="#0ec0e2"
			rounded={12}
			disableInteraction
			showCloseButton
			showNavigation
			showNavigationNumber
			badgeContent={(curr, tot) => `${curr} of ${tot}`}
			scrollDuration={300}
			lastStepNextButton={
				<button
					onClick={handleClose}
					style={{
						padding: '10px 14px',
						borderRadius: 10,
						border: '1px solid #0ec0e2',
						background: '#0ec0e2',
						color: '#fff',
						cursor: 'pointer',
						lineHeight: 1.2,
					}}
				>
					Finish
				</button>
			}
			nextButton={
				<button
					style={{
						padding: '10px 14px',
						borderRadius: 10,
						border: '1px solid #0ec0e2',
						background: 'transparent',
						color: '#0ec0e2',
						cursor: 'pointer',
						lineHeight: 1.2,
					}}
				>
					Next →
				</button>
			}
			prevButton={
				<button
					style={{
						padding: '10px 14px',
						borderRadius: 10,
						border: '1px solid #d0d7de',
						background: 'transparent',
						color: '#333',
						cursor: 'pointer',
						lineHeight: 1.2,
					}}
				>
					← Back
				</button>
			}
			{...tourProps}
		/>
	)
}
